import { useTimezone } from '@app/core/hooks/useTimezone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, ListItemButton, ListItemText, Menu, MenuList } from '@mui/material';
import { ComponentProps, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { useIntl } from 'react-intl';
import { dateSelectorPresetMessages } from './date-selector-messages';
import {
  type DateSelectorPresetId,
  DATE_SELECTOR_PRESET_ORDER,
  DAY_OFFSET_PRESETS,
  getPresetDate,
  isCustomDateSelectorPreset,
} from './date-selector-presets';
import { SingleDatePicker } from './single-date-picker';
import { SingleDateSelectorTriggerLabel } from './SingleDateSelectorTriggerLabel';

export interface SingleDateSelectorProps {
  /** Current date (for controlled mode and dialog initial value). */
  value?: Date | null;
  /** Current preset id when value was set by a preset (for trigger label). */
  presetId?: DateSelectorPresetId | null;
  /** Called when the user selects a preset or applies a date from the dialog. */
  onChange: (date: Date, presetId: DateSelectorPresetId) => void;
  /** When true, shows the dropdown chevron icon on the trigger. Default false. */
  showChevrons?: boolean;
  /** Props passed to the trigger Button (variant, size, sx, etc.). */
  buttonProps?: Partial<ComponentProps<typeof Button>>;
  /** Which days are disabled in the calendar (e.g. before retention, after today). */
  dayPickerDisabled?: ComponentProps<typeof DayPicker>['disabled'];
}

/** Preset ids that are shown as list items (excludes 'selectDate', which is represented by the calendar on the right). */
const PRESET_IDS_IN_MENU = DATE_SELECTOR_PRESET_ORDER.filter((id) => id !== 'selectDate');

/** Short format for the 5 "previous days" in the menu: weekday + date, e.g. "lun. 16 feb." */
const shortDateFormatWithWeekday = {
  weekday: 'short' as const,
  month: 'short' as const,
  day: 'numeric' as const,
};

/** Capitalizes only the first character (e.g. "lun. 16 feb." → "Lun. 16 feb."). */
function capitalizeFirstLetter(str: string): string {
  if (!str) {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Single-date selector: presets list on the left (today, yesterday, 5 days in short format)
 * and a calendar picker always visible on the right for any other date.
 * Uses timezone from TimezoneCtxProvider for preset date calculations.
 */
export function SingleDateSelector({
  value,
  presetId,
  onChange,
  showChevrons = false,
  buttonProps,
  dayPickerDisabled,
}: SingleDateSelectorProps) {
  const { $t, formatDate, locale } = useIntl();
  const tz = useTimezone();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const applyPreset = (id: DateSelectorPresetId) => {
    const date = getPresetDate(id, tz);

    if (date) {
      onChange(date, id);
    }

    handleMenuClose();
  };

  const handleSingleApply = (date: Date) => {
    onChange(date, 'selectDate');
    handleMenuClose();
  };

  /** No-op when calendar is inline: Cancel does not close the menu so user can still pick a preset. */
  const handleSingleClose = () => {
    // noop
  };

  const menuContent = (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <MenuList dense sx={{ py: 0, pr: 1.5, borderRight: 1, borderColor: 'divider' }}>
        {PRESET_IDS_IN_MENU.map((id) => {
          const label = DAY_OFFSET_PRESETS.includes(id)
            ? (() => {
                const date = getPresetDate(id, tz);
                return date
                  ? capitalizeFirstLetter(formatDate(date, shortDateFormatWithWeekday) ?? '')
                  : $t(dateSelectorPresetMessages[id]);
              })()
            : $t(dateSelectorPresetMessages[id]);
          const isSelected = presetId === id;
          return (
            <ListItemButton
              key={id}
              selected={!isCustomDateSelectorPreset(id) && isSelected}
              onClick={() => applyPreset(id)}
              sx={
                isSelected
                  ? {
                      fontWeight: 500,
                      backgroundColor: 'primary.main',
                    }
                  : undefined
              }
            >
              <ListItemText
                primary={label}
                primaryTypographyProps={isSelected ? { fontWeight: 500 } : undefined}
              />
            </ListItemButton>
          );
        })}
      </MenuList>
      <SingleDatePicker
        open={menuOpen}
        onClose={handleSingleClose}
        onChange={handleSingleApply}
        initialDate={value ?? null}
        disabled={dayPickerDisabled}
        tz={tz}
        locale={locale}
      />
    </Box>
  );

  return (
    <>
      <Button
        variant="outlined"
        {...buttonProps}
        onClick={handleTriggerClick}
        sx={{
          width: 'fit-content',
          display: 'flex',
          px: 0,
          ...(showChevrons ? { justifyContent: 'space-between' } : {}),
          ...(menuOpen
            ? {
                position: 'relative',
                zIndex: (theme) => theme.zIndex.modal + 1,
              }
            : {}),
          ...(typeof buttonProps?.sx === 'object' && !Array.isArray(buttonProps.sx)
            ? buttonProps.sx
            : {}),
        }}
      >
        <SingleDateSelectorTriggerLabel value={value} presetId={presetId} />
        {showChevrons && <ExpandMoreIcon fontSize="small" />}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'transparent' } },
          root: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
          },
          paper: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 10 },
          },
        }}
        disableScrollLock
      >
        {menuContent}
      </Menu>
    </>
  );
}
